import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from './contexts/AuthContext';
import { RegistrationPendingScreen } from './components/RegistrationPendingScreen';
import MfaGate from './components/security/MfaGate';
import MfaNagBanner from './components/security/MfaNagBanner';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';

// Auth pages
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';

// Legal — public document pages + first-login acceptance gate
import { LegalDocPage } from './pages/legal/LegalDocPage';

// Sign-in hop for the customer user manual (served by the userManual function)
import UserManualRedirect from './pages/UserManualRedirect';

// Protected pages
import { Dashboard } from './pages/Dashboard';
import { Certifications } from './pages/Certifications';
import { QuickTestsPage } from './pages/QuickTestsPage';
import { Flows } from './pages/Flows';
import { FlowEditor } from './pages/FlowEditor';
import { PrimitiveFlowEditor } from './pages/PrimitiveFlowEditor';
import { Tests } from './pages/Tests';
import { Analytics } from './pages/Analytics';
// Legacy reports imports removed — old Reports / ExecutionReport /
// OperatorBenchmarking / LocationHealth pages are no longer routed.

// Reports rebuild — persona-lens architecture
// Data Performance Lens is the primary lens; Connectivity Lens kept as legacy.
import ReportsHome from './pages/reports/ReportsHome';
import BuilderPage from './pages/reports/BuilderPage';
import ReportSubscriptionsPage from './pages/reports/ReportSubscriptionsPage';
import RunDetail from './pages/reports/RunDetail';
import SloSettings from './pages/reports/SloSettings';

import { Locations } from './pages/Locations';
import Edges from './pages/Edges';
import { AuditLogs } from './pages/AuditLogs';
import { ESimInventory } from './pages/ESimInventory';
import ESimProvisioning from './pages/ESimProvisioning';
import { Organization } from './pages/Organization';
import { Settings } from './pages/Settings';
import BillingSuccessPage from './pages/BillingSuccessPage';
import BillingCancelPage from './pages/BillingCancelPage';
import { PrimitiveLibraryPage } from './pages/PrimitiveLibrary';
import DeveloperPortal from './pages/DeveloperPortal';
// Note: Executions and Schedules pages are deprecated - routes redirect to /tests

// Test component pages
import { FormComponents } from './pages/FormComponents';
import { DataDisplayComponents } from './pages/DataDisplayComponents';
import { FeedbackComponents } from './pages/FeedbackComponents';
import { LayoutComponents } from './pages/LayoutComponents';
import { AdditionalComponents } from './pages/AdditionalComponents';

// Admin pages
import EdgeDevices from './pages/admin/EdgeDevices';
import ConnectivityPage from './pages/ConnectivityPage';
import EdgeDeviceDetail from './pages/admin/EdgeDeviceDetail';
import Deployments from './pages/admin/Deployments';
import DeviceProvisioning from './pages/admin/DeviceProvisioning';
import { AuditLogsPage } from './pages/admin/AuditLogsPage';
import { PrimitivesAdminPage } from './pages/admin/PrimitivesAdmin';
import QaAutomationPage from './pages/admin/QaAutomationPage';
import { SystemPage } from './pages/admin/SystemPage';
import { SystemSettingsPage } from './pages/admin/SystemSettingsPage';
import { UsersPage } from './pages/admin/UsersPage';
import { OrganizationsPage } from './pages/admin/OrganizationsPage';
import { SubscriptionPlansPage } from './pages/admin/SubscriptionPlansPage';
import { BillingAddonsPage } from './pages/admin/BillingAddonsPage';
import ModemModelsPage from './pages/admin/ModemModelsPage';
import { NotificationsPage } from './pages/NotificationsPage';
import NetworksPage from './pages/admin/NetworksPage';
import { LocationAnalytics } from './pages/admin/LocationAnalytics';
import { SystemApiKeysPage } from './pages/admin/SystemApiKeysPage';
import { AdminMcpKeysPage } from './pages/admin/AdminMcpKeysPage';
import { ExecutionMonitorPage } from './pages/admin/ExecutionMonitorPage';

// Auth components
import { ProtectedRoute } from './components/auth/ProtectedRoute';

// Keyboard shortcuts
import { useGlobalKeyboardShortcuts } from './hooks/useKeyboardShortcuts';
import { KeyboardShortcutHelp } from './components/KeyboardShortcutHelp';
import { useDocumentTitle } from './hooks/useDocumentTitle';

/**
 * Backward-compat redirect from the old per-execution route to the new
 * unified run-detail page. Wrapped to use `useParams` outside `<App>` root.
 */
function RedirectExecutionToRun() {
  const params = (window.location.pathname.match(/\/reports\/execution\/([^/?#]+)/) ?? [])[1];
  if (!params) return <Navigate to="/reports" replace />;
  return <Navigate to={`/reports/runs/${params}`} replace />;
}

/**
 * Backward-compat redirect from the retired standalone matching-runs page
 * (`/reports/builder/runs?cfg&dims&preset`) into the unified builder in its
 * drilled state — the old `dims` param maps to the new `drill` param.
 */
function LegacyBuilderRunsRedirect() {
  const search = new URLSearchParams(window.location.search);
  const out = new URLSearchParams();
  const preset = search.get('preset');
  const cfg = search.get('cfg');
  const dims = search.get('dims');
  if (preset) out.set('preset', preset);
  if (cfg) out.set('cfg', cfg);
  if (dims) out.set('drill', dims);
  const qs = out.toString();
  return <Navigate to={`/reports/builder${qs ? `?${qs}` : ''}`} replace />;
}

function AppContent() {
  // State for help modal
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const { currentUser, activeOrganization, activeOrganizationId, needsLegalAcceptance, needsOrgSelection, loading, mfaBlocking, mfaNag } = useAuth();
  const location = useLocation();
  // Don't cover the public legal pages with the gate — the modal links open
  // them in a new tab, which must remain readable while the gate is active.
  const onLegalPage = location.pathname.startsWith('/legal');
  const navigate = useNavigate();
  const prevOrgIdRef = useRef<string | null>(null);

  // When the active org changes (not on initial load), navigate to home so the
  // user doesn't get stuck on a page they no longer have permissions to view.
  useEffect(() => {
    if (
      prevOrgIdRef.current !== null &&
      activeOrganizationId !== null &&
      prevOrgIdRef.current !== activeOrganizationId
    ) {
      navigate('/');
    }
    prevOrgIdRef.current = activeOrganizationId;
  }, [activeOrganizationId]);

  // Initialize global keyboard shortcuts
  const { shortcuts } = useGlobalKeyboardShortcuts(() => setIsHelpModalOpen((prev) => !prev));

  // Reactive document.title: "<Org> · <Page> · SimCheck"
  useDocumentTitle();

  // Registration approval gate (free-trial vetting): a signed-in user whose
  // active org is pending review is blocked from the app until approved. Legal
  // pages stay reachable so the gate's links still work.
  if (currentUser && !loading && !onLegalPage && activeOrganization?.approvalStatus === 'pending') {
    return <RegistrationPendingScreen />;
  }

  // Two-factor gate: block the app until the session satisfies the org's 2FA
  // requirement. Legal/public pages stay reachable so their links keep working.
  const showMfaGate = !!currentUser && !loading && mfaBlocking && !onLegalPage;

  return (
    <>
      {mfaNag && !onLegalPage && <MfaNagBanner />}
      <Routes key={activeOrganizationId ?? 'no-org'}>
        {/* Public routes - Auth pages */}
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />

        {/* Public legal pages — reachable without authentication */}
        <Route path="/legal/terms" element={<LegalDocPage docKey="terms" />} />
        <Route path="/legal/privacy" element={<LegalDocPage docKey="privacy" />} />
        <Route path="/legal/eula" element={<LegalDocPage docKey="eula" />} />
        <Route path="/legal/acceptable-use" element={<LegalDocPage docKey="acceptable-use" />} />

        {/* User manual sign-in hop — mints the /user-manual session cookie, then leaves the SPA */}
        <Route
          path="/manual"
          element={
            <ProtectedRoute>
              <UserManualRedirect />
            </ProtectedRoute>
          }
        />

        {/* Protected routes - Main application */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/certifications"
          element={
            <ProtectedRoute>
              <Certifications />
            </ProtectedRoute>
          }
        />
        <Route
          path="/quick-tests"
          element={
            <ProtectedRoute>
              <QuickTestsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/notifications"
          element={
            <ProtectedRoute>
              <NotificationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/flows"
          element={
            <ProtectedRoute>
              <Flows />
            </ProtectedRoute>
          }
        />
        <Route
          path="/flows/:flowId"
          element={
            <ProtectedRoute>
              <FlowEditor />
            </ProtectedRoute>
          }
        />
        {/* Redirect old /modems route to admin location analytics */}
        <Route
          path="/modems"
          element={<Navigate to="/admin/location-analytics" replace />}
        />
        <Route
          path="/esim-inventory"
          element={
            <ProtectedRoute>
              <ESimInventory />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/provisioning"
          element={
            <ProtectedRoute requiredRole="super_admin">
              <ESimProvisioning />
            </ProtectedRoute>
          }
        />
        {/* Clusters live inside the Locations page — a cluster is just a saved
            group of locations, so it needs the same hierarchy and the same
            picker. A standalone page duplicated both. */}
        <Route
          path="/clusters"
          element={<Navigate to="/locations" replace />}
        />
        <Route
          path="/tests"
          element={
            <ProtectedRoute>
              <Tests />
            </ProtectedRoute>
          }
        />
        {/* Redirect old /executions route to /tests */}
        <Route
          path="/executions"
          element={<Navigate to="/tests" replace />}
        />
        {/* Redirect old /schedules route to /tests (consolidated) */}
        <Route
          path="/schedules"
          element={<Navigate to="/tests" replace />}
        />
        {/* Reports rebuild — persona-lens architecture (Phase 1).
            The old 5-tab Reports page is preserved at /reports/legacy until the
            sweep PR removes it. All other lenses share  so
            filters survive drill-down via URL query string. */}
        <Route
          path="/reports"
          element={
            <ProtectedRoute>
              
                <ReportsHome />
              
            </ProtectedRoute>
          }
        />
        {/* Legacy lens routes — keep redirects so existing bookmarks/links still work */}
        <Route path="/reports/data" element={<Navigate to="/reports/builder?preset=data_performance_by_country" replace />} />
        <Route path="/reports/coverage" element={<Navigate to="/reports/builder?preset=coverage_by_operator" replace />} />
        <Route path="/reports/benchmark" element={<Navigate to="/reports/builder?preset=modem_comparison" replace />} />
        <Route path="/reports/fleet" element={<Navigate to="/reports/builder?preset=fleet_overview" replace />} />
        <Route
          path="/reports/builder"
          element={
            <ProtectedRoute>
              <BuilderPage />
            </ProtectedRoute>
          }
        />
        <Route path="/reports/builder/runs" element={<LegacyBuilderRunsRedirect />} />
        <Route
          path="/reports/subscriptions"
          element={
            <ProtectedRoute>
              <ReportSubscriptionsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports/runs/:executionId"
          element={
            <ProtectedRoute>
              <RunDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports/settings"
          element={
            <ProtectedRoute>
              <SloSettings />
            </ProtectedRoute>
          }
        />
        {/* Backward-compat: old per-execution route redirects to new run detail */}
        <Route
          path="/reports/execution/:executionId"
          element={<RedirectExecutionToRun />}
        />
        {/* Redirect old /analytics to /reports */}
        <Route
          path="/analytics"
          element={<Navigate to="/reports" replace />}
        />
        <Route
          path="/locations"
          element={
            <ProtectedRoute>
              <Locations />
            </ProtectedRoute>
          }
        />
        <Route
          path="/primitives"
          element={
            <ProtectedRoute>
              <PrimitiveLibraryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/audit-logs"
          element={
            <ProtectedRoute>
              <AuditLogs />
            </ProtectedRoute>
          }
        />
        <Route
          path="/organization"
          element={
            <ProtectedRoute>
              <Organization />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/billing/success"
          element={
            <ProtectedRoute>
              <BillingSuccessPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/billing/cancel"
          element={
            <ProtectedRoute>
              <BillingCancelPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/developer-portal"
          element={
            <ProtectedRoute>
              <DeveloperPortal />
            </ProtectedRoute>
          }
        />

        {/* Protected routes - Admin pages */}
        {/* System admin routes (super_admin + support) */}
        {/* Tenant edge routes. /edges is the two-section Edges page (#270):
            the org's own devices, plus the SimCheck network's locations as
            capacity — tenants never see shared hardware as devices. It is open
            to EVERY plan, since it is how a customer sees where their tests
            run; only provisioning (bringing your own hardware) keeps the
            Business/Enterprise gate. The cross-org global device view stays at
            /admin/edge-devices. */}
        <Route
          path="/edges"
          element={
            <ProtectedRoute>
              <Edges />
            </ProtectedRoute>
          }
        />
        <Route
          path="/edges/connectivity"
          element={
            <ProtectedRoute requiredPlan={['business', 'enterprise']}>
              <ConnectivityPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/edges/:id"
          element={
            <ProtectedRoute requiredPlan={['business', 'enterprise']}>
              <EdgeDeviceDetail />
            </ProtectedRoute>
          }
        />
        {/* /edges/deployments intentionally removed — deployments stay
            super-admin-only at /admin/deployments. Tenants don't need to
            manage release rollouts to their (or anyone's) fleet from the
            tenant nav. */}
        {/* Bringing your own hardware is the Business/Enterprise half of
            /edges — the route gate mirrors the nav gate (#270). */}
        <Route
          path="/edges/provisioning"
          element={
            <ProtectedRoute requiredPlan={['business', 'enterprise']}>
              <DeviceProvisioning />
            </ProtectedRoute>
          }
        />
        {/* Legacy /admin/* device routes — same pages, super-admin gate
            preserved for back-compat. Will be redirected to /edges/* in a
            follow-up once nav + bookmarks have caught up. */}
        <Route
          path="/admin/edge-devices"
          element={
            <ProtectedRoute requiredRole="support">
              <EdgeDevices />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/edge-devices/:id"
          element={
            <ProtectedRoute requiredRole="support">
              <EdgeDeviceDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/deployments"
          element={
            <ProtectedRoute requiredRole="super_admin">
              <Deployments />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/device-provisioning"
          element={
            <ProtectedRoute requiredRole="super_admin">
              <DeviceProvisioning />
            </ProtectedRoute>
          }
        />
<Route
          path="/admin/location-analytics"
          element={
            <ProtectedRoute requiredRole="support">
              <LocationAnalytics />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/audit-logs"
          element={
            <ProtectedRoute requiredRole="support">
              <AuditLogsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/execution-monitor"
          element={
            <ProtectedRoute requiredRole="super_admin">
              <ExecutionMonitorPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/primitives"
          element={
            <ProtectedRoute requiredRole="super_admin">
              <PrimitivesAdminPage />
            </ProtectedRoute>
          }
        />
        {/*
         * QA Automation — tenant-gated (NOT super-admin-only). Visible to org
         * admins of orgs with settings.features.qaAutomation === true.
         * See plans/qa-automation-tenant-and-admin-page.md.
         */}
        <Route
          path="/admin/qa-automation"
          element={
            <ProtectedRoute requireOrgFeature="qaAutomation">
              <QaAutomationPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/primitives/:primitiveId/edit-flow"
          element={
            <ProtectedRoute requiredRole="super_admin">
              <PrimitiveFlowEditor />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/system"
          element={
            <ProtectedRoute requiredRole="super_admin">
              <SystemPage />
            </ProtectedRoute>
          }
        />
                <Route
                  path="/admin/users"
                  element={
                    <ProtectedRoute requiredRole="super_admin">
                      <UsersPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/organizations"
                  element={
                    <ProtectedRoute requiredRole="super_admin">
                      <OrganizationsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/subscription-plans"
                  element={
                    <ProtectedRoute requiredRole="super_admin">
                      <SubscriptionPlansPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/billing-addons"
                  element={
                    <ProtectedRoute requiredRole="super_admin">
                      <BillingAddonsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/modem-models"
                  element={
                    <ProtectedRoute requiredRole="super_admin">
                      <ModemModelsPage />
                    </ProtectedRoute>
                  }
                />
                {/* Tenant-readable modem catalogue (Phase 8). Same page;
                    tenant role hides write affordances at the component
                    level. Firestore rules already permit read for all
                    authenticated users. */}
                <Route
                  path="/edges/modems"
                  element={
                    <ProtectedRoute>
                      <ModemModelsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/networks"
                  element={
                    <ProtectedRoute requiredRole="super_admin">
                      <NetworksPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/settings"
                  element={
                    <ProtectedRoute requiredRole="super_admin">
                      <SystemSettingsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/system-api-keys"
                  element={
                    <ProtectedRoute requiredRole="super_admin">
                      <SystemApiKeysPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/mcp-keys"
                  element={
                    <ProtectedRoute requiredRole="super_admin">
                      <AdminMcpKeysPage />
                    </ProtectedRoute>
                  }
                />

        {/* Protected routes - Test component pages */}
        <Route
          path="/form-components"
          element={
            <ProtectedRoute>
              <FormComponents />
            </ProtectedRoute>
          }
        />
        <Route
          path="/data-display-components"
          element={
            <ProtectedRoute>
              <DataDisplayComponents />
            </ProtectedRoute>
          }
        />
        <Route
          path="/feedback-components"
          element={
            <ProtectedRoute>
              <FeedbackComponents />
            </ProtectedRoute>
          }
        />
        <Route
          path="/layout-components"
          element={
            <ProtectedRoute>
              <LayoutComponents />
            </ProtectedRoute>
          }
        />
        <Route
          path="/additional-components"
          element={
            <ProtectedRoute>
              <AdditionalComponents />
            </ProtectedRoute>
          }
        />

        {/* Catch-all route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* Keyboard Shortcut Help Modal */}
      <KeyboardShortcutHelp
        shortcuts={shortcuts}
        isHelpModalOpen={isHelpModalOpen}
        setIsHelpModalOpen={setIsHelpModalOpen}
      />

      {/* Two-factor gate — blocks the app until the second factor is satisfied. */}
      {showMfaGate && <MfaGate />}

      {/* Legal consent is captured ONLY at account creation (self-serve signup
          and invite acceptance — both stamp the hardened record at profile
          creation). Admin-created users are bound via the organization's MSA
          (authorized-users clause); there is deliberately no gate, modal, or
          login-time consent UI. Decision 2026-06-11. */}
    </>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;


